/**
 * CallCentreMeService
 * Caller portal surface: who I am, my queue, the call card, dial + outcome, self-claim, callbacks,
 * referrals (handoffs routed to me as ministry owner), reminders, my stats, (Stage 3) my phone system shift and
 * (Stage 6) AI assist: availability, a suggestion from my notes with the care check, and a personal opener.
 * Route: callcentre/api/callcentre/me (portal role or CallCentre.Calls.Make; per-user rate limit)
 *
 * AI assist answers 503 `ai_unavailable` when the helper is off (hide the action and read the availability again)
 * and 503 `ai_failed` when the AI service failed or timed out (carry on without it). Suggestions are never applied
 * by the server: the caller accepts each part, and the suggestion goes back on logOutcome as `aiSuggestion`.
 *
 * `getMe` only needs a signed-in user and answers `hasAccess: false` for people without access (the portal
 * shows "ask an admin"). Every other call answers 403 `no_access` for them. Error codes to branch on arrive in
 * CCMSError / errors[0] (CALL_CENTRE_ERROR_CODES): `not_assigned`, `contact_closed`, `campaign_not_active`,
 * `outside_call_window`, `do_not_call`, `note_required`, `callback_required`, `consent_required`, ...
 */
import { BaseService } from '../base/BaseService';
import { unwrapCallCentreResponse as unwrap, callCentreOkOrSuccess as okOrSuccess, callCentreListOrEmpty as listOrEmpty, } from './unwrapCallCentreResponse';
/** Caller portal service (JWT). */
export class CallCentreMeService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? CallCentreMeService.DEFAULT_SERVICE_PATH,
            basePath: CallCentreMeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ---- me + profile ----
    /** GET api/callcentre/me (signed in only): profile, my campaigns, counts and portal settings. */
    async getMe() {
        return unwrap(await this.getMeRaw(), 'Call centre profile not found');
    }
    getMeRaw() {
        return this.http.get(this.buildUrl());
    }
    /** PUT api/callcentre/me/profile: my reminder preferences. */
    async saveProfile(request) {
        return unwrap(await this.saveProfileRaw(request), 'Failed to save your profile');
    }
    saveProfileRaw(request) {
        return this.http.put(this.buildUrl('profile'), request);
    }
    // ---- queue + call card ----
    /** GET api/callcentre/me/queue?campaignId=&includeWaiting=: my queue in call order (queueGroup 1-4). */
    async getQueue(query = {}) {
        return listOrEmpty(await this.getQueueRaw(query));
    }
    getQueueRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('queue', { campaignId: query.campaignId, includeWaiting: query.includeWaiting }));
    }
    /** GET api/callcentre/me/next?campaignId=: the next call card, or null when nothing is due. */
    async getNext(campaignId) {
        return (await this.getNextRaw(campaignId)).data ?? null;
    }
    getNextRaw(campaignId) {
        return this.http.get(this.buildUrlWithParams('next', { campaignId }));
    }
    /** GET api/callcentre/me/contacts/{id}: the call card for one of my contacts. */
    async getCallCard(contactId) {
        return unwrap(await this.getCallCardRaw(contactId), `Contact ${contactId} not found`);
    }
    getCallCardRaw(contactId) {
        return this.http.get(this.buildUrl(`contacts/${contactId}`));
    }
    /**
     * POST api/callcentre/me/contacts/{id}/dial: creates the attempt. Stage 3: with an open shift the church phone
     * system rings the caller's phone (`usedPhoneSystem: true`, do not open `telUri`); otherwise open `telUri` and show
     * `message` when given (e.g. the phone system did not answer).
     */
    async dial(contactId) {
        return unwrap(await this.dialRaw(contactId), 'Failed to start the call');
    }
    dialRaw(contactId) {
        return this.http.post(this.buildUrl(`contacts/${contactId}/dial`), {});
    }
    /**
     * POST api/callcentre/me/contacts/{id}/outcome: log the call (answers, journey, handoff, detail changes).
     * Stage 5: a Care referral says whether it went to Acts Care in `handoffMessage`.
     * A journey step booked onto an Acts Care course date (`linkedCourseDateID` on a course-linked step) needs
     * `journey.hasConsent: true` (else 400 `journey.hasConsent`, nothing saved); `journeyMessage` says what happened.
     */
    async logOutcome(contactId, request) {
        return unwrap(await this.logOutcomeRaw(contactId, request), 'Failed to log the call');
    }
    logOutcomeRaw(contactId, request) {
        return this.http.post(this.buildUrl(`contacts/${contactId}/outcome`), request);
    }
    /** POST api/callcentre/me/contacts/{id}/release: give the contact back to the pool. */
    async releaseContact(contactId) {
        return okOrSuccess(await this.releaseContactRaw(contactId));
    }
    releaseContactRaw(contactId) {
        return this.http.post(this.buildUrl(`contacts/${contactId}/release`), {});
    }
    /** POST api/callcentre/me/campaigns/{id}/claim: take contacts from a self-claim campaign's pool. */
    async claim(campaignId, request = {}) {
        return unwrap(await this.claimRaw(campaignId, request), 'Failed to claim contacts');
    }
    claimRaw(campaignId, request = {}) {
        return this.http.post(this.buildUrl(`campaigns/${campaignId}/claim`), request);
    }
    // ---- journey: Acts Care course dates ----
    /**
     * GET api/callcentre/me/journey/steps/{stepId}/course-dates: upcoming dates of the Acts Care course the step books
     * onto (BookingTargetTypeID ActsCareCourse, BookingTargetRef = CourseID). 400 when the step is not linked to a
     * course; 503 `course_service_unavailable` when Acts Care can't be reached (mark it as booked and add the date later).
     */
    async getStepCourseDates(stepId) {
        return listOrEmpty(await this.getStepCourseDatesRaw(stepId));
    }
    getStepCourseDatesRaw(stepId) {
        return this.http.get(this.buildUrl(`journey/steps/${stepId}/course-dates`));
    }
    // ---- callbacks + referrals ----
    /** GET api/callcentre/me/callbacks: every callback, due or not. */
    async getCallbacks() {
        return listOrEmpty(await this.getCallbacksRaw());
    }
    getCallbacksRaw() {
        return this.http.get(this.buildUrl('callbacks'));
    }
    /** GET api/callcentre/me/referrals?openOnly=: handoffs routed to me as ministry owner. */
    async getReferrals(openOnly) {
        return listOrEmpty(await this.getReferralsRaw(openOnly));
    }
    getReferralsRaw(openOnly) {
        return this.http.get(this.buildUrlWithParams('referrals', { openOnly }));
    }
    /**
     * POST api/callcentre/me/referrals/{id}/status: accept / done / decline a referral. Stage 5: `joined: true` on a
     * Ministry referral marked Done adds the person to the ministry (read `integrationMessage` on the result).
     */
    async setReferralStatus(handoffId, request) {
        return unwrap(await this.setReferralStatusRaw(handoffId, request), 'Failed to update the referral');
    }
    setReferralStatusRaw(handoffId, request) {
        return this.http.post(this.buildUrl(`referrals/${handoffId}/status`), request);
    }
    // ---- reminders ----
    /** GET api/callcentre/me/reminders?includeRead=: my reminders + unread count. */
    async getReminders(includeRead) {
        return unwrap(await this.getRemindersRaw(includeRead), 'Reminders not found');
    }
    getRemindersRaw(includeRead) {
        return this.http.get(this.buildUrlWithParams('reminders', { includeRead }));
    }
    /** POST api/callcentre/me/reminders/{id}/read */
    async markReminderRead(reminderId) {
        return okOrSuccess(await this.markReminderReadRaw(reminderId));
    }
    markReminderReadRaw(reminderId) {
        return this.http.post(this.buildUrl(`reminders/${reminderId}/read`), {});
    }
    /** POST api/callcentre/me/reminders/{id}/dismiss */
    async dismissReminder(reminderId) {
        return okOrSuccess(await this.dismissReminderRaw(reminderId));
    }
    dismissReminderRaw(reminderId) {
        return this.http.post(this.buildUrl(`reminders/${reminderId}/dismiss`), {});
    }
    // ---- stats ----
    /** GET api/callcentre/me/stats */
    async getStats() {
        return unwrap(await this.getStatsRaw(), 'Stats not found');
    }
    getStatsRaw() {
        return this.http.get(this.buildUrl('stats'));
    }
    // ---- Stage 3: shift (which phone the church phone system rings) ----
    /** GET api/callcentre/me/shift: the desk phones I can pick, my own extension and my open shift. */
    async getShift() {
        return unwrap(await this.getShiftRaw(), 'Shift not found');
    }
    getShiftRaw() {
        return this.http.get(this.buildUrl('shift'));
    }
    /**
     * POST api/callcentre/me/shift/start: a desk station (`stationID`) or my own extension (`useMyExtension`).
     * Answers 409 when the desk phone is in use by someone else.
     */
    async startShift(request) {
        return unwrap(await this.startShiftRaw(request), 'Failed to start your shift');
    }
    startShiftRaw(request) {
        return this.http.post(this.buildUrl('shift/start'), request);
    }
    /** POST api/callcentre/me/shift/end: end my open shift. */
    async endShift() {
        return okOrSuccess(await this.endShiftRaw());
    }
    endShiftRaw() {
        return this.http.post(this.buildUrl('shift/end'), {});
    }
    // ---- Stage 6: AI assist ----
    /** GET api/callcentre/me/ai/availability: which AI helpers I can use right now (all false while AI is off for me). */
    async getAiAvailability() {
        return unwrap(await this.getAiAvailabilityRaw(), 'AI availability not found');
    }
    getAiAvailabilityRaw() {
        return this.http.get(this.buildUrl('ai/availability'));
    }
    /**
     * POST api/callcentre/me/contacts/{id}/ai/assist (callAssist or careFlag on): from my notes, a suggested outcome,
     * next journey step, ministry, callback time and tidy note, plus the care check (`careLevel` none / concern /
     * urgent; urgent alerts the supervisors). 400 when the notes are empty or longer than 4000 characters.
     */
    async aiAssist(contactId, request) {
        return unwrap(await this.aiAssistRaw(contactId, request), 'No AI suggestion came back');
    }
    aiAssistRaw(contactId, request) {
        return this.http.post(this.buildUrl(`contacts/${contactId}/ai/assist`), request);
    }
    /** POST api/callcentre/me/contacts/{id}/ai/opener (scriptAssist on): a personal first line for this call. Nothing is stored. */
    async aiOpener(contactId) {
        return unwrap(await this.aiOpenerRaw(contactId), 'No opener came back');
    }
    aiOpenerRaw(contactId) {
        return this.http.post(this.buildUrl(`contacts/${contactId}/ai/opener`), {});
    }
    /**
     * GET api/callcentre/me/campaigns/{id}/guide (271i): the campaign's conversation guide with every step and option, merge
     * fields left in (null when the campaign has none). The call card already carries the guide filled in for its person;
     * use this to load it ahead of time and key a device copy on guideID + version + revision.
     */
    async getCampaignGuide(campaignId) {
        return (await this.getCampaignGuideRaw(campaignId)).data ?? null;
    }
    getCampaignGuideRaw(campaignId) {
        return this.http.get(this.buildUrl(`campaigns/${campaignId}/guide`));
    }
}
CallCentreMeService.DEFAULT_SERVICE_PATH = 'callcentre';
CallCentreMeService.BASE_PATH = 'api/callcentre/me';
//# sourceMappingURL=CallCentreMeService.js.map